import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import AuthShell from "@/pages/auth/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { useL } from "@/context/I18nContext";

const initial = {
  first_name: "", last_name: "", email: "", phone_prefix: "+49", phone: "",
  country: "DE", preferred_language: "de", password: "", confirm_password: "",
  accept_terms: false, accept_privacy: false, marketing_consent: false,
};

export default function Registrazione() {
  const { register } = useAuth();
  const L = useL();
  const navigate = useNavigate();
  const [form, setForm] = useState(initial);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password !== form.confirm_password) { setError(L("Le password non coincidono", "Passwords do not match", "Die Passwörter stimmen nicht überein")); return; }
    if (form.password.length < 8) { setError(L("La password deve avere almeno 8 caratteri", "The password must be at least 8 characters", "Das Passwort muss mindestens 8 Zeichen lang sein")); return; }
    if (!form.accept_terms || !form.accept_privacy) { setError(L("Devi accettare Termini e Privacy", "You must accept the Terms and Privacy", "Sie müssen die AGB und den Datenschutz akzeptieren")); return; }
    setLoading(true);
    try {
      await register(form);
      toast.success(L("Registrazione completata", "Registration completed", "Registrierung abgeschlossen"));
      navigate("/portal", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title={L("Crea un account", "Create an account", "Konto erstellen")}
      subtitle={L("Registrati per aprire una pratica e gestirla nel portale riservato.", "Register to open a case and manage it in the secure portal.", "Registrieren Sie sich, um einen Fall zu eröffnen und ihn im geschützten Portal zu verwalten.")}
      footer={<>{L("Hai già un account?", "Already have an account?", "Sie haben bereits ein Konto?")} <Link to="/login" className="text-cyanx hover:text-white">{L("Accedi", "Sign in", "Anmelden")}</Link></>}
    >
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-slate-300">{L("Nome", "First name", "Vorname")}</Label><Input required value={form.first_name} onChange={set("first_name")} data-testid="register-first-name" className="mt-1.5 bg-navy-paper border-white/10" /></div>
          <div><Label className="text-slate-300">{L("Cognome", "Last name", "Nachname")}</Label><Input required value={form.last_name} onChange={set("last_name")} data-testid="register-last-name" className="mt-1.5 bg-navy-paper border-white/10" /></div>
        </div>
        <div><Label className="text-slate-300">{L("Email", "Email", "E-Mail")}</Label><Input type="email" required value={form.email} onChange={set("email")} data-testid="register-email" className="mt-1.5 bg-navy-paper border-white/10" /></div>
        <div className="grid grid-cols-3 gap-3">
          <div><Label className="text-slate-300">{L("Prefisso", "Prefix", "Vorwahl")}</Label><Input value={form.phone_prefix} onChange={set("phone_prefix")} data-testid="register-prefix" className="mt-1.5 bg-navy-paper border-white/10" /></div>
          <div className="col-span-2"><Label className="text-slate-300">{L("Telefono", "Phone", "Telefon")}</Label><Input value={form.phone} onChange={set("phone")} data-testid="register-phone" className="mt-1.5 bg-navy-paper border-white/10" /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-slate-300">{L("Paese", "Country", "Land")}</Label><Input value={form.country} onChange={set("country")} data-testid="register-country" className="mt-1.5 bg-navy-paper border-white/10" /></div>
          <div><Label className="text-slate-300">{L("Lingua", "Language", "Sprache")}</Label>
            <select value={form.preferred_language} onChange={set("preferred_language")} data-testid="register-language" className="mt-1.5 w-full h-10 rounded-md bg-navy-paper border border-white/10 px-3 text-sm text-white">
              <option value="de">Deutsch</option><option value="it">Italiano</option><option value="en">English</option>
            </select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-slate-300">{L("Password", "Password", "Passwort")}</Label><Input type="password" required value={form.password} onChange={set("password")} data-testid="register-password" className="mt-1.5 bg-navy-paper border-white/10" /></div>
          <div><Label className="text-slate-300">{L("Conferma", "Confirm", "Bestätigen")}</Label><Input type="password" required value={form.confirm_password} onChange={set("confirm_password")} data-testid="register-confirm" className="mt-1.5 bg-navy-paper border-white/10" /></div>
        </div>
        <div className="space-y-2.5 pt-1">
          <label className="flex items-start gap-2.5 text-sm text-slate-300 cursor-pointer">
            <Checkbox checked={form.accept_terms} onCheckedChange={(v) => setForm({ ...form, accept_terms: !!v })} data-testid="register-terms" className="mt-0.5" />
            <span>{L("Accetto i", "I accept the", "Ich akzeptiere die")} <Link to="/termini" className="text-cyanx">{L("Termini", "Terms", "AGB")}</Link></span>
          </label>
          <label className="flex items-start gap-2.5 text-sm text-slate-300 cursor-pointer">
            <Checkbox checked={form.accept_privacy} onCheckedChange={(v) => setForm({ ...form, accept_privacy: !!v })} data-testid="register-privacy" className="mt-0.5" />
            <span>{L("Accetto la", "I accept the", "Ich akzeptiere die")} <Link to="/privacy" className="text-cyanx">{L("Privacy Policy", "Privacy Policy", "Datenschutzerklärung")}</Link></span>
          </label>
          <label className="flex items-start gap-2.5 text-sm text-slate-400 cursor-pointer">
            <Checkbox checked={form.marketing_consent} onCheckedChange={(v) => setForm({ ...form, marketing_consent: !!v })} data-testid="register-marketing" className="mt-0.5" />
            <span>{L("Acconsento a comunicazioni di marketing (facoltativo)", "I consent to marketing communications (optional)", "Ich stimme Marketing-Mitteilungen zu (optional)")}</span>
          </label>
        </div>
        {error && <p className="text-sm text-red-400" data-testid="register-error">{error}</p>}
        <Button type="submit" disabled={loading} className="w-full bg-gold text-navy hover:bg-gold-hover h-11 font-medium" data-testid="register-submit">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : L("Registrati", "Register", "Registrieren")}
        </Button>
      </form>
    </AuthShell>
  );
}
